import { useMemo, useState } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { DeleteIcon, ModeEditIcon, MoneyIcon, VisibilityIcon } from "@l2r-front/l2r-icons"
import { Campaign } from "@l2r-front/l2r-proptypes"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { I18N_NAMESPACE, useCampaignProject, STATUS_DELIVERED, STATUS_IN_PROGRESS } from "@l2r-front/l2r-shared-campaign"
import { CardWithThumbnail } from "@l2r-front/l2r-ui"
import { useNavigateWithSearchParams } from "@l2r-front/l2r-utils"

import { CampaignModules } from "../../constants/modules"
import { statusesSettings } from "../../constants/status"
import { CampaignEditModal } from "../../containers/CampaignEditModal"
import { useCampaignDelete } from "../../hooks/mutations/useCampaignDelete"
import { useCampaignTexts } from "../../hooks/useCampaignTexts"
import { useIsCampaignWriter } from "../../hooks/useIsCampaignWriter"

import * as Styled from "./CampaignCard.styled"

export interface CampaignCardProps {
    campaign: Campaign
    onMouseEnter?: () => void
    onMouseLeave?: () => void
    onDeleteCampaign?: (_campaignId) => void
}

export function CampaignCard(props: CampaignCardProps) {

    const {
        campaign,
        onMouseEnter,
        onMouseLeave,
    } = props

    const { data: project } = useCampaignProject()
    const { t } = useTranslation([I18N_NAMESPACE])

    const module = CampaignModules.find((module) => module.module === campaign.campaign_type)
    const ModuleIcon = module?.icon
    const status = statusesSettings[campaign.status]
    const StatusIcon = status?.icon
    const navigate = useNavigateWithSearchParams()
    const { setAlert } = useAlertsDispatchContext()
    const isCampaignWriter = useIsCampaignWriter(campaign)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const campaignTexts = useCampaignTexts(campaign.campaign_type, t)

    const deleteMutation = useCampaignDelete(campaign.uuid, {}, {
        errorSnackbarMessage: t(I18N_NAMESPACE, "components.campaignMenu.delete.snackbar.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "components.campaignMenu.delete.snackbar.success"),
    })

    const campaignCost = useMemo(() => {
        return `${campaign.cost} ${project?.currency.symbol ?? ""}`
    }, [campaign.cost, project?.currency])

    const handleDeleteCampaign = () => {
        setAlert({
            alertTitle: t(I18N_NAMESPACE, "components.campaignMenu.delete.confirm.label"),
            alertText: campaignTexts?.["deleteCampaignDesc"],
            cancelText: t(I18N_NAMESPACE, "components.campaignMenu.delete.confirm.cancel"),
            confirmText: t(I18N_NAMESPACE, "components.campaignMenu.delete.confirm.validate"),
            callback: () => {
                deleteMutation.mutate({})
            },
        })
    }

    return <>
        <CardWithThumbnail
            title={campaign.name}
            date={campaign.date_start}
            secondaryIcon={MoneyIcon}
            secondaryText={Number(campaign.cost) ?? campaignCost}
            chips={[
                {
                    icon: <ModuleIcon />,
                    label: t(I18N_NAMESPACE, module?.labelKey),
                },
                {
                    icon: <StatusIcon />,
                    label: t(I18N_NAMESPACE, status?.statusLabel),
                },
            ]}
            onClick={() => navigate(campaign.uuid)}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            menuConfig={[
                {
                    displayed: true,
                    id: "details-campaign",
                    label: t(I18N_NAMESPACE, "components.campaignMenu.details.label"),
                    icon: <VisibilityIcon color="common/black_states" />,
                    props: {
                        onClick: () => navigate(campaign.uuid),
                    },
                },
                {
                    displayed: campaign.status === STATUS_IN_PROGRESS && isCampaignWriter,
                    id: "edit-campaign",
                    label: t(I18N_NAMESPACE, "components.campaignMenu.edit.label"),
                    icon: <ModeEditIcon color="common/black_states" />,
                    props: {
                        onClick: () => setEditModalOpen(true),
                    },
                },
                {
                    displayed: campaign.status === STATUS_IN_PROGRESS && isCampaignWriter,
                    id: "delete-campaign",
                    label: t(I18N_NAMESPACE, "components.campaignMenu.delete.label"),
                    icon: <DeleteIcon color="common/black_states" />,
                    props: {
                        onClick: handleDeleteCampaign,
                    },
                }]}
            greydOut={campaign.status === STATUS_DELIVERED}
        >
            <Styled.ColorContainer>
                <Styled.Color color={campaign.color} />
            </Styled.ColorContainer>
        </CardWithThumbnail>
        <CampaignEditModal
            campaign={campaign}
            open={editModalOpen}
            onClose={() => setEditModalOpen(false)}
        />
    </>
}